import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { filter, map, Observable, of, switchMap } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUser } from '../../../store/app.selectors';

import { skipMessagingKey } from '../../../core/services/http.context';
import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { WorkspaceFeatures } from '../../../modules/chat/interfaces/IWorkspace';
import { AlertModalComponent } from '../../shared/components/alert/alert.component';
import { IUserProfile } from '../../user/interfaces/IUserProfile';
import { IChannel } from '../interfaces/IChannel';
import { DealerSettingName } from '../interfaces/IDealerSettings';
import { IMessage } from '../interfaces/IMessage';
import { getMessageTranslation } from '../store/chat.actions';
import { AppStateWithChat } from '../store/chat.reducer';
import { PermissionsService } from './permissions.service';

@Injectable()
export class TranslaterService {
  user: IUserProfile | null = null;
  selectedChannel: IChannel | null = null;
  languageCodes = [
    { key: 'en', name: 'english' },
    { key: 'es', name: 'spanish' },
    { key: 'it', name: 'italian' },
    { key: 'fr', name: 'french' },
    { key: 'de', name: 'german' },
    { key: 'zh', name: 'chinese' },
    { key: 'ar', name: 'arabic' },
    { key: 'az', name: 'azerbaijani' },
    { key: 'eu', name: 'basque' },
    { key: 'bn', name: 'bengali' },
    { key: 'ca', name: 'catalan' },
    { key: 'da', name: 'danish' },
    { key: 'nl', name: 'dutch' },
    { key: 'fi', name: 'finnish' },
    { key: 'el', name: 'greek' },
    { key: 'he', name: 'hebrew' },
    { key: 'hi', name: 'hinglish' },
    { key: 'hu', name: 'hungarian' },
    { key: 'id', name: 'indonesian' },
    { key: 'kk', name: 'kazakh' },
    { key: 'ne', name: 'nepali' },
    { key: 'no', name: 'norwegian' },
    { key: 'pt', name: 'portuguese' },
    { key: 'po', name: 'polish' },
    { key: 'ro', name: 'romanian' },
    { key: 'ru', name: 'russian' },
    { key: 'sl', name: 'slovene' },
    { key: 'sv', name: 'swedish' },
    { key: 'tg', name: 'tajik' },
    { key: 'tr', name: 'turkish' },
  ];
  constructor(
    private httpClient: HttpClient,
    private permissionsService: PermissionsService,
    private store: Store<AppStateWithChat>,
    private env: MessagingEnvironmentService,
    private dialog: MatDialog
  ) {
    this.store.select(selectUser).subscribe((user) => {
      this.user = user;
    });

    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ selectedChannel }) => {
        this.selectedChannel = selectedChannel;
      });
  }

  translateText(message: string, lang: string): Observable<string> {
    return this.httpClient
      .post<{ translationText: string }>(
        `${this.env.env.textTranslationApiUrl}/translate`,
        {
          text: message,
          targetLanguage: lang,
        },
        {
          context: skipMessagingKey(),
        }
      )
      .pipe(
        map((res) => {
          return res.translationText;
        })
      );
  }

  getChannelLang(channel: IChannel): Observable<{ lang: string | null }> {
    // Early return if conditions aren't met
    if (!channel || !this.verifyHaveTranslationPermissions || !channel?.messages?.length) {
      return of({ lang: null });
    }

    if (channel?.lang && !channel.unreadMessages) {
      return of({ lang: channel.lang });
    }

    // Filter and order messages
    const messagesOrdered = channel.messages.filter((message) => !message.outcoming).reverse();

    if (!messagesOrdered.length) {
      return of({ lang: null });
    }

    // Prepare translation request
    const translationRequest = {
      text: messagesOrdered[0].body,
      targetLanguage: this.dealerLanguge,
    };

    return this.httpClient
      .post<{ originalLanguage: string }>(`${this.env.env.textTranslationApiUrl}/translate`, translationRequest, {
        context: skipMessagingKey(),
      })
      .pipe(
        map((res) => ({ lang: res.originalLanguage })),
        switchMap((res) => this.handleLangChangeConfirmation(channel, res))
      );
  }
  setChannelLang(channelUID: string, lang: string) {
    return this.httpClient.patch<unknown>(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/channel/${channelUID}/update`,
      {
        language: lang,
      }
    );
  }

  private handleLangChangeConfirmation(
    channel: IChannel,
    detectedLang: { lang: string }
  ): Observable<{ lang: string | null }> {
    if (channel.lang && channel.lang !== detectedLang.lang) {
      const language = this.languageCodes.find((e) => e.key === detectedLang.lang);
      return this.dialog
        .open(AlertModalComponent, {
          data: {
            title: 'New lang detected',
            message: `Are you sure to change the channel lang to ${language?.name ?? detectedLang.lang}?`,
          },
        })
        .afterClosed()
        .pipe(map((result: boolean) => (result ? detectedLang : { lang: channel.lang ?? null })));
    }
    return of(detectedLang);
  }

  translateMessages(messages: IMessage[], channel: IChannel): void {
    if (this.verifyCanTranslate) {
      for (const message of [...messages].reverse()) {
        if (!message.translation) {
          this.store.dispatch(getMessageTranslation({ message, channel }));
        }
      }
    }
  }

  get verifyCanTranslateWithLocalSetting(): boolean {
    return this.verifyCanTranslate && !(this.selectedChannel?.hideTranslateSetting ?? false);
  }

  get verifyCanTranslate(): boolean {
    return !!(
      this.verifyHaveTranslationPermissions &&
      this.selectedChannel?.lang &&
      this.selectedChannel.lang !== this.dealerLanguge
    );
  }

  get dealerLanguge(): string {
    const language = this.user?.dealerSettings?.length
      ? this.user.dealerSettings.find((e) => e.key === DealerSettingName.language)
      : null;
    return language?.value || 'en';
  }

  get verifyHaveTranslationPermissions(): boolean {
    return (
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canTranslateMessages]) &&
      this.permissionsService.checkDealerSetting(DealerSettingName.messageTranslationEnabled)
    );
  }
}
